/* SiteDetailsViewRenderer.js */

"use strict";
const _ = require("lodash");
const Forms = require("@gluenetworks/forms");
const FormsComponent = Forms.ui.component;

const timeZonesJSON = require("timezones.json");

const SiteCFUtility = require("../../../../lib/SiteCfUtility");
const InheritedFieldUtility = require("../../../../lib/InheritedFieldUtility");

const readOnlyFieldIds = [
    "parentName",
    "deviceCount",
    "childSiteCount",
    "updatedOn",
    "updatedBy",
    "createdOn",
    "createdBy",
    "customFieldSources",
    "credsId",
];

class SiteDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("site-details-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._isDirty = false;

        this._formsComponent = new FormsComponent();
        this._siteCfUtility = new SiteCFUtility();

        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Save",
            name: "SaveSiteDetails",
            handler: (this._onSiteDetailsSave = this._onSiteDetailsSave.bind(this)),
        });
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Cancel",
            name: "CancelSiteDetails",
            handler: (this._onSiteDetailsCancel = this._onSiteDetailsCancel.bind(this)),
        });
        this._autoAssignSites = this._autoAssignSites.bind(this);
        this._renderingForm = false;
        this._debouncedOnSitePropertiesChanged = _.debounce(
            this._onSitePropertiesChanged.bind(this),
            1e3,
            false
        );
        this._toggleActions();
        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _registerListeners() {
        this._model.on("Dirty", (this._onDirty = this._onDirty.bind(this)));
        this._model.on("SitePropertiesChanged", this._debouncedOnSitePropertiesChanged);
        this._formsComponent.on(
            "PendingPatchesChanged",
            (this._onFormsPendingPatchesChanged = _.debounce(
                this._onFormsPendingPatchesChanged.bind(this),
                500,
                false
            ))
        );
        this._model.on(
            "SiteFeedUpdated",
            (this._onSiteFeedUpdated = this._onSiteFeedUpdated.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Dirty", this._onDirty);
        this._model.removeListener("SitePropertiesChanged", this._debouncedOnSitePropertiesChanged);
        this._formsComponent.removeListener(
            "PendingPatchesChanged",
            this._onFormsPendingPatchesChanged
        );
        this._model.removeListener("SiteFeedUpdated", this._onSiteFeedUpdated);
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._debouncedOnSitePropertiesChanged.cancel();
        this._unregisterListeners();
        this._onFormsPendingPatchesChanged.cancel();
        let promArr = [this._formsComponent.shutdown()];

        return Promise.all(promArr).then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
                this._isDirty = undefined;
                this._formsComponent = undefined;
                this._renderingForm = undefined;
                this._siteCfUtility = undefined;
                this._customFieldHierarchyObj = undefined;
                this._discoveryLevelInheritanceUtility = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "SiteDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(withLoading = false) {
        if (!this._rootElem.hasChildNodes()) {
            this._rootElem.appendChild(this._formsComponent.renderer.rootElement);
        }
        const promises = [this._model.whenModelIsReady()];
        if (withLoading) {
            promises.push(this._model._modalUserFeedback.displayLoading());
        }

        return Promise.race(promises)
            .then(() => {
                return this.renderSiteDetailsProm();
            })
            .catch((err) => err);
    }

    renderSiteDetailsProm(withLoading = false) {
        const promises = [];
        if (withLoading) {
            promises.push(this._model._modalUserFeedback.displayLoading());
        }
        return Promise.all(promises).then(() => {
            this._toggleActions();
            return this._renderSiteDetails();
        });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE ACTION METHODS            */
    /*******************************************/

    _showViewActions() {
        if (this._isDirty) {
            this._model.gameEditor.showViewAction("SaveSiteDetails");
            this._model.gameEditor.showViewAction("CancelSiteDetails");
        }
    }

    _hideViewActions() {
        this._model.gameEditor.hideViewAction("SaveSiteDetails");
        this._model.gameEditor.hideViewAction("CancelSiteDetails");
    }

    _disableViewActions() {
        this._model.gameEditor.disableViewAction("SaveSiteDetails");
        this._model.gameEditor.disableViewAction("CancelSiteDetails");
    }

    _enableViewActions() {
        this._model.gameEditor.enableViewAction("SaveSiteDetails");
        this._model.gameEditor.enableViewAction("CancelSiteDetails");
    }

    _toggleActions() {
        if (this._isDirty) {
            this._showViewActions();
        } else {
            this._hideViewActions();
        }
    }

    /*******************************************/
    /* END PRIVATE ACTION METHODS              */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    _renderSiteDetails() {
        return Promise.resolve().then(
            () => {
                let jtype = this._getJtype();
                let pristineValue = this._getPristineValue();

                this._renderedPathList = _.cloneDeep(this._model._pathList);
                this._formsComponent.setTypeInformation(jtype, true);
                this._formsComponent.setPristineValue(pristineValue, true);

                this._renderingForm = true;
                return this._formsComponent.renderer.render().then(() => {
                    this._customFieldHierarchyObj = this._siteCfUtility.getCustomFieldInheritance(
                        this._model._site,
                        this._model._parentSite,
                        this._model._customFieldColumnDef
                    );
                    // Delay so rendering can complete
                    new Promise((resolve) => {
                        setTimeout(resolve, 500);
                    }).then(() => {
                        this._renderingForm = false;
                        this._discoveryLevelInheritanceUtility = new InheritedFieldUtility(
                            "discoveryLevel",
                            "discovery_level",
                            this._model._site,
                            this._model._parentSite,
                            this._formsComponent,
                            "setDiscoveryLevelFromParentSite",
                            undefined,
                            this._model._siteNameMap
                        );
                        this._discoveryLevelInheritanceUtility.generateIcon();
                        this._updateCustomBooleanWidth();
                        if (this._model._parentSite) {
                            this._siteCfUtility.updateCustomFieldsHierarchy(
                                this._rootElem,
                                this._model._site,
                                this._formsComponent
                            );
                        }
                        const form = this._formsComponent.getValue();
                        this._enableDisableAutoAssign(form);
                    });
                });
            },
            (err) => {
                this._logger.warn(
                    {error: err},
                    "SiteDetailsViewRenderer._renderSiteDetails() error."
                );
            }
        );
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START HANDLER METHODS                   */
    /*******************************************/

    _enableDisableAutoAssign(form) {
        if (!this._model || this._model._isRootSite || !this._rootElem) {
            return;
        }
        const autoAssignProperty = this._rootElem.querySelector(
            ".rendered_control.auto_assign_property"
        );
        const autoAssignRegex = this._rootElem.querySelector(".rendered_control.auto_assign_regex");
        if (!autoAssignProperty || !autoAssignRegex) {
            return;
        }

        if (_.get(form, "autoAssignContainer.autoAssignEnabled", false)) {
            autoAssignProperty.classList.remove("autoassigned-disabled");
            autoAssignRegex.classList.remove("autoassigned-disabled");
        } else {
            autoAssignProperty.classList.add("autoassigned-disabled");
            autoAssignRegex.classList.add("autoassigned-disabled");
        }
    }

    _onFormsPendingPatchesChanged() {
        if (!this._model || !this._formsComponent) {
            return;
        }
        if (!this._renderingForm) {
            let form = this._formsComponent.getValue();

            /* These below lines are specific to SiteDetailsForm */
            /* Not to be copied to other forms when copying this generic _onFormsPendingPatchesChanged method */
            /* Site Specific code - Start */
            this._enableDisableAutoAssign(form);

            // Keep ghost custom fields (CF no longer in CF definitions but not removed from site entity)
            let customFieldsFromForm = form.customFieldValues
                ? _.cloneDeep(form.customFieldValues)
                : {};
            const pristineCustomFieldValues = _.get(
                this.model._site.getPristineProperties(),
                "customFieldValues",
                {}
            );
            Object.keys(pristineCustomFieldValues).map((uuid) => {
                let formVal = _.get(customFieldsFromForm, uuid);
                if (formVal === undefined) {
                    customFieldsFromForm[uuid] = pristineCustomFieldValues[uuid];
                }
            });

            // These form properties are introduced since the pristineProperties now do not match the form jtype structure
            const formProperties = this._model._isRootSite
                ? {
                      name: "name",
                      codeName: "codeName",
                      credsName: "credsName",
                  }
                : {
                      name: "name",
                      codeName: "codeName",
                      description: "description",
                      adminContact: "adminContact",
                      technicalContact: "technicalContact",
                      location: "location",
                      locationType: "locationType",
                      address: "address",
                      country: "country",
                      latitude: "latitude",
                      longitude: "longitude",
                      timezone: "timezone",
                      autoAssignEnabled: "autoAssignContainer.autoAssignEnabled",
                      autoAssignProperty: "autoAssignContainer.autoAssignProperty",
                      autoAssignRegex: "autoAssignContainer.autoAssignRegex",
                      credsName: "credsName",
                  };

            /* Site Specific code - End */

            const setProperty = (name) => {
                if (!name) {
                    return;
                }
                let formPropertyValue = _.get(form, formProperties[name]);
                let pristinePropertyValue = _.get(this.model._site.getPristineProperties(), name);

                let methodName = "set" + _.upperFirst(name);

                // Specific case for credsName
                /* Site Specific code - Start */
                if (name === "credsName") {
                    methodName = "setCredsByName";
                }
                /* Site Specific code - End */

                if (this.isEmpty(formPropertyValue) && this.isEmpty(pristinePropertyValue)) {
                    if (_.get(this._model._site, name) !== pristinePropertyValue) {
                        _.invoke(this._model._site, methodName, pristinePropertyValue);
                    }
                } else {
                    if (formPropertyValue !== pristinePropertyValue) {
                        _.invoke(this._model._site, methodName, formPropertyValue);
                    } else if (_.get(this._model._site, name) !== pristinePropertyValue) {
                        _.invoke(this._model._site, methodName, pristinePropertyValue);
                    }
                }
            };

            const setProperties = (names = []) => names.forEach(setProperty);

            const propertiesToSet = this._model._isRootSite
                ? ["name", "codeName", "credsName"]
                : [
                      "name",
                      "codeName",
                      "description",
                      "adminContact",
                      "technicalContact",
                      "location",
                      "locationType",
                      "address",
                      "country",
                      "latitude",
                      "longitude",
                      "timezone",
                      "autoAssignEnabled",
                      "autoAssignProperty",
                      "autoAssignRegex",
                      "credsName",
                  ];

            setProperties(propertiesToSet);

            if (!this._model._site.discoveryLevelFrom) {
                this._model._site.setDiscoveryLevel(form.discoveryLevel);
            }

            this._setCustomFields(customFieldsFromForm);
            const parentId = this._model._site.getParentId() || "/";
            const formParentId = form.path;

            if (parentId !== formParentId) {
                if (_.isEmpty(formParentId) || formParentId === "/") {
                    this._model._site.setParentSiteRelationship(
                        this._model._userSession.activeOrganization.id
                    );
                } else {
                    this._model._site.setParentSiteRelationship(formParentId);
                }
            } else {
                this._model._gameEditor.clearPendingRelationshipsFor(
                    "parentSite",
                    this._model._site,
                    false
                );
            }
        }
    }

    _setCustomFields(customFieldsFromForm) {
        const updatedCustomFields = Object.assign({}, this._model._site.getCustomFieldValues());
        _.keys(customFieldsFromForm).map((uuid) => {
            if (customFieldsFromForm[uuid] !== undefined) {
                updatedCustomFields[uuid] = customFieldsFromForm[uuid];
            }
            if (
                this._customFieldHierarchyObj[uuid] &&
                this._customFieldHierarchyObj[uuid].updateToInheritance === "inherited"
            ) {
                delete updatedCustomFields[uuid];
            }
        });
        // if (!_.isEmpty(updatedCustomFields)) {
        this._model._site.setDirectlyAssignedCustomFieldValues(updatedCustomFields);
        // }
        const updatedCfIds = Object.keys(updatedCustomFields);
        const pristineSourceCfIds = Object.keys(
            _.get(this._model._site, "pristineProperties.customFieldSources", {})
        );
        const restoreInheritedSource = (cf) => {
            const siteCfSources = Object.assign({}, this._model._site.customFieldSources);
            siteCfSources[cf] = this._model._site.pristineProperties.customFieldSources[cf];
            this._model._site.setCustomFieldSources(siteCfSources);
            const siteCfs = Object.assign({}, this._model._site.getCustomFieldValues());
            siteCfs[cf] = this._model._site.pristineProperties.customFieldValues[cf];
            this._model._site.setCustomFieldValues(siteCfs);
        };
        Object.keys(this._customFieldHierarchyObj).forEach((cf) => {
            if (updatedCfIds.indexOf(cf) < 0 && pristineSourceCfIds.indexOf(cf) >= 0) {
                restoreInheritedSource(cf);
            }
        });
        // Inherited CFs whose definition is no longer form-visible (unshared,
        // disabled or deleted) must keep their pristine source; otherwise the call
        // above silently converts them to direct assignments and the site goes dirty.
        // See PE-14176 for more details.
        pristineSourceCfIds.forEach((cf) => {
            if (!this._customFieldHierarchyObj[cf]) {
                restoreInheritedSource(cf);
            }
        });
    }

    isEmpty(value) {
        return (
            value === undefined ||
            value === null ||
            Number.isNaN(value) ||
            (Array.isArray(value) && value.every((str) => str === "")) ||
            (typeof value === "string" && value.length === 0) ||
            (typeof value === "object" && Object.keys(value).length === 0)
        );
    }

    async _onSitePropertiesChanged(pristineProperties, before) {
        let isFormRerendered = false;
        const credsName = _.get(this._model._site, "credsName");
        if (
            (credsName && credsName.search("_Site") >= 0) ||
            Object.keys(this._model.credOptions).indexOf(pristineProperties.credsName) < 0
        ) {
            this._model.credOptions = this._model._getCredOptions(this._model._credEtys);
            this._formsComponent.setTypeInformation(this._getJtype(), true);
            await this._formsComponent.renderer.render();
            isFormRerendered = true;
        }
        if (!this._renderingForm) {
            let formVal = this._formsComponent.getValue(); // Data from Jtype form
            let sitePropsVal = this._getPristineValue(); // Data from _pristineProperties i.e. sync'd with BE

            let updateReadOnlyFields = () => {
                _.keys(pristineProperties).map((id) => {
                    if (!_.isUndefined(sitePropsVal[id]) && readOnlyFieldIds.indexOf(id) === -1) {
                        sitePropsVal[id] = formVal[id];
                    } else {
                        if (!_.isEqual(pristineProperties[id], this._model._site[id])) {
                            this._model._site.setProperty(id, pristineProperties[id]);
                        }
                    }
                });

                this._formsComponent.setPristineValue(sitePropsVal);
                return Promise.resolve();
            };

            let updateInheritanceIcons = () => {
                this._customFieldHierarchyObj = this._siteCfUtility.getCustomFieldInheritance(
                    this._model._site,
                    this._model._parentSite,
                    this._model._customFieldColumnDef
                );
                if (this._model._parentSite) {
                    this._siteCfUtility.updateCustomFieldsHierarchy(
                        this._rootElem,
                        this._model._site,
                        this._formsComponent
                    );
                    this._discoveryLevelInheritanceUtility.setParentEntity(this._model._parentSite);
                    this._discoveryLevelInheritanceUtility.setEntity(this._model._site);
                    this._discoveryLevelInheritanceUtility.setForm(this._formsComponent);
                    if (isFormRerendered) {
                        this._discoveryLevelInheritanceUtility.generateIcon();
                    } else {
                        this._discoveryLevelInheritanceUtility.updateIconTitle();
                    }
                }
            };
            if (isFormRerendered) {
                updateInheritanceIcons();
                this._updateCustomBooleanWidth();
            }
            if (pristineProperties.parentId !== _.get(this._model, "_parentSite.id")) {
                this._model._gameEditor._siteEntity
                    .loadSite(
                        this._model._userSession.activeOrganization.id,
                        pristineProperties.parentId
                    )
                    .then((site) => {
                        if (!this._model) {
                            return;
                        }
                        this._model._parentSite = site;
                        updateInheritanceIcons();
                    });
            }

            // Usual isDirty is limited to its pendingPatches non-empty aspect here
            // because the only relationship is parentSite, has no impact on form
            // and can arrive late if connection slow causing veil to come up
            if (this._model._site.pendingPatches) {
                let isReadOnlyUpdate = true;
                _.keys(pristineProperties).map((id) => {
                    // No need to run these checks if isReadOnlyUpdate is already false
                    if (!isReadOnlyUpdate) {
                        return;
                    }

                    if (
                        readOnlyFieldIds.indexOf(id) === -1 &&
                        !_.isEqual(_.get(pristineProperties, id, null), _.get(before, id, null))
                    ) {
                        isReadOnlyUpdate = false;
                    }
                });

                if (!isReadOnlyUpdate) {
                    this._model.gameEditor.runQuickConfirmationActionWorkflow(
                        {
                            message:
                                "This site has been updated by another user. Do you want to refresh its details? All changes will be lost.",
                            title: "Update Confirmation Check",
                            confirmDetails: {
                                text: "Yes",
                                type: "text",
                            },
                        },
                        (this._onSiteDetailsCancelAction =
                            this._onSiteDetailsCancelAction.bind(this)),
                        updateReadOnlyFields
                    );
                } else {
                    updateReadOnlyFields();
                }
            } else {
                this._formsComponent.setPristineValue(sitePropsVal);
            }
        }
    }

    _onSiteFeedUpdated() {
        if (!this._model || !this._formsComponent) {
            return;
        }
        const formPathVal = this._formsComponent.getValue().path;
        const inList = Object.values(this._model._pathList).indexOf(formPathVal);
        if (inList < 0) {
            this._resetPathPatch();
        }
        let rendered = Promise.resolve();
        if (!_.isEqual(this._renderedPathList, this._model._pathList)) {
            this._renderedPathList = _.cloneDeep(this._model._pathList);
            this._formsComponent.setTypeInformation(this._getJtype(), true);
            rendered = this._formsComponent.renderer.render();
        }
        return rendered
            .then(() => {
                if (!this._model) {
                    return undefined;
                }
                if (_.isEmpty(formPathVal) || formPathVal === "/") {
                    return Promise.resolve(undefined);
                }
                return this._model._gameEditor._siteEntity.loadSite(
                    this._model._userSession.activeOrganization.id,
                    formPathVal
                );
            })
            .then((site) => {
                if (!this._model) {
                    return;
                }
                this._model._parentSite = site;
                this._updateCustomBooleanWidth();
                this._customFieldHierarchyObj = this._siteCfUtility.getCustomFieldInheritance(
                    this._model._site,
                    this._model._parentSite,
                    this._model._customFieldColumnDef
                );
                if (this._model._parentSite) {
                    this._siteCfUtility.updateCustomFieldsHierarchy(
                        this._rootElem,
                        this._model._site,
                        this._formsComponent
                    );
                    this._discoveryLevelInheritanceUtility.setSiteNameMap(this._model._siteNameMap);
                    this._discoveryLevelInheritanceUtility.setParentEntity(site);
                    this._discoveryLevelInheritanceUtility.setEntity(this._model._site);
                    this._discoveryLevelInheritanceUtility.generateIcon();
                }
                return Promise.resolve();
            });
    }

    _resetPathPatch() {
        const patchArr = this._formsComponent.getPendingPatches();
        if (!patchArr) {
            return;
        }
        const pathPatchIndex = patchArr.findIndex((patch) => {
            return patch.path === "/path";
        });
        // Without this guard splice(-1, 1) drops an unrelated pending patch when the
        // form has no /path patch at all, which is the common case here.
        if (pathPatchIndex < 0) {
            return;
        }
        patchArr.splice(pathPatchIndex, 1);
        this._formsComponent._setPendingPatches(patchArr);
    }

    _onDirty(dirtyState) {
        this._isDirty = dirtyState;

        let onBeforeUnloadFunc = () => {
            return this._model._modalUserFeedback
                .displayConfirmation("Would you like to Save or Discard your changes?", {
                    title: "Unsaved Changes",
                    confirmDetails: {
                        text: "Save",
                        type: "additive",
                    },
                    rejectDetails: {
                        text: "Discard",
                        type: "text",
                    },
                })
                .then(() => {
                    if (this._model.isSiteFormValid(true)) {
                        return this._onSiteDetailsSave().then(() => undefined); // it will not close the tab if it will have something in result
                    }
                })
                .catch((err) => {
                    if (err.userRejected) {
                        return Promise.resolve(this._model._site.clearPendingPatches(true))
                            .then(() => this._model._modalUserFeedback.hideDisplayed())
                            .then(() => this._model._gameEditor.getTabCmpnt().setDirty(false))
                            .then(() => undefined);
                    } else {
                        console.error("Error in onBeforeUnloadFunc! Err:", err);
                    }
                });
        };

        if (this._model.isDirty()) {
            this._model._gameEditor.setOnBeforeUnload(onBeforeUnloadFunc);
        } else {
            this._model._gameEditor.clearOnBeforeUnload();
        }

        this._toggleActions();
    }

    async _onSiteDetailsSave() {
        //jshint ignore:line
        try {
            await this.model.isSiteFormValid();
        } catch (err) {
            return this.model.displayError(err);
        }

        this._disableViewActions();

        const pendingPatches = this._model._site.getPendingPatches() || [];
        const autoAssignChanged = !!pendingPatches.find(
            (i) =>
                i.path === "/autoAssignEnabled" ||
                i.path === "/autoAssignProperty" ||
                i.path === "/autoAssignRegex"
        );

        this._model._autoAssignUnchecked = !!pendingPatches.find(
            (i) => i.path === "/autoAssignEnabled" && i.value === false
        );
        let previewAccept = true;

        if (autoAssignChanged) {
            previewAccept = await this._model._previewAutoAssignSites();
        }

        if (previewAccept) {
            return this._model
                .saveSite()
                .then(async () => {
                    //jshint ignore:line
                    this._enableViewActions();

                    let jtype;
                    let pristineValue = this._getPristineValue();

                    if (
                        Object.keys(this._model.credOptions).indexOf(pristineValue.credsName) < 0 ||
                        (pristineValue.credsName === "_Site" &&
                            _.get(this._model, "_parentSite.credsName"))
                    ) {
                        this._model.credOptions = this._model._getCredOptions(
                            this._model._credEtys
                        );
                        jtype = this._getJtype();
                        this._formsComponent.setTypeInformation(jtype, true);
                        this._formsComponent.setPristineValue(pristineValue, true);
                    }

                    if (autoAssignChanged) {
                        return this._model._gameEditor
                            .runQuickConfirmationActionWorkflow(
                                {
                                    message:
                                        "Do you want to update device assignments now? Otherwise, device assignments will be updated when discovery is run.",
                                    title: "Confirmation",
                                    confirmDetails: {
                                        text: "Update now",
                                        type: "additive",
                                    },
                                    rejectDetails: {
                                        text: "Later",
                                        type: "text",
                                    },
                                },
                                this._autoAssignSites
                            )
                            .finally(() => {
                                this._model._modalUserFeedback.hideDisplayed();
                                return true;
                            })
                            .catch((error) => {
                                if (!error.userRejected) {
                                    this._logger.warn(
                                        {error},
                                        "SiteDetailsViewRenderer._onSiteDetailsSave() error"
                                    );
                                }
                                return false;
                            });
                    }
                })
                .then(() => {
                    let formSiteParentId = this._formsComponent.getValue().path;
                    if (formSiteParentId) {
                        return this._model._gameEditor._siteEntity.loadSite(
                            this._model._userSession.activeOrganization.id,
                            formSiteParentId
                        );
                    }
                    return Promise.resolve(undefined);
                })
                .then((parentSite) => {
                    if (!this._model) {
                        return;
                    }
                    this._model._parentSite = parentSite;
                    this._customFieldHierarchyObj = this._siteCfUtility.getCustomFieldInheritance(
                        this._model._site,
                        this._model._parentSite,
                        this._model._customFieldColumnDef
                    );
                    if (this._model._parentSite) {
                        this._siteCfUtility.updateIconTitles(this._rootElem);
                        this._discoveryLevelInheritanceUtility.setParentEntity(parentSite);
                        this._discoveryLevelInheritanceUtility.setEntity(this._model._site);
                        this._discoveryLevelInheritanceUtility.updateIconTitle();
                    }
                })
                .catch(() => {
                    this._enableViewActions();
                    return false;
                });
        }

        this._enableViewActions();
        return false;
    }

    _autoAssignSites() {
        this._model._modalUserFeedback.displayLoading();
        return this._model._autoAssignSites();
    }

    _onSiteDetailsCancel() {
        this._model.gameEditor.runQuickConfirmationActionWorkflow(
            {
                message: "Are you sure you want to cancel? All changes will be lost.",
                title: "Cancel Confirmation Check",
                confirmDetails: {
                    text: "Yes",
                    type: "text",
                },
            },
            (this._onSiteDetailsCancelAction = this._onSiteDetailsCancelAction.bind(this))
        );
    }

    async _onSiteDetailsCancelAction() {
        //jshint ignore:line
        await this._model.resetSiteProperties();
        return this._renderSiteDetails();
    }

    _hideLeftFooterContainer() {
        let leftSelector = document.querySelector("left-container");
        leftSelector.style.display = "none";
    }

    _showLeftFooterContainer() {
        let querySelector = document.querySelector("left-container");
        querySelector.style.display = "block";
    }

    /*******************************************/
    /* END HANDLER METHODS                     */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _toLocaleString(val) {
        let d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    _getPristineValue() {
        if (!this._model) {
            return;
        }
        const orgName = this._model._gameEditor._userSession.activeOrganization.name;
        let pristineProperties = this.model._site.getPristineProperties(),
            pristineValue = {};

        if (this._model._site) {
            pristineValue.name = pristineProperties.name;
            pristineValue.codeName = pristineProperties.codeName;
            pristineValue.description = pristineProperties.description;
            const pathValue =
                this._model._pathList[
                    pristineProperties.path && pristineProperties.path !== pristineProperties.name
                        ? `${
                              pristineProperties.path
                                  .split("/")
                                  .slice(0, -1)
                                  .join("/")
                                  .replace("/" + orgName, "") || "/"
                          }`
                        : "/"
                ];

            pristineValue.path = pathValue || this._model._pathList["/"];
            pristineValue.parentName = pristineProperties.parentName || "/";
            pristineValue.adminContact = pristineProperties.adminContact || [];
            pristineValue.technicalContact = pristineProperties.technicalContact || [];
            pristineValue.location = pristineProperties.location;
            pristineValue.locationType = pristineProperties.locationType;
            pristineValue.address = pristineProperties.address;
            pristineValue.country = pristineProperties.country;
            pristineValue.latitude = pristineProperties.latitude;
            pristineValue.longitude = pristineProperties.longitude;
            pristineValue.timezone = pristineProperties.timezone;
            pristineValue.autoAssignContainer = {};
            pristineValue.autoAssignContainer.autoAssignEnabled =
                pristineProperties.autoAssignEnabled || false;
            pristineValue.autoAssignContainer.autoAssignProperty =
                pristineProperties.autoAssignProperty || "NAME";
            pristineValue.autoAssignContainer.autoAssignRegex =
                pristineProperties.autoAssignRegex || "";
            pristineValue.deviceCount = pristineProperties.deviceCount || "0";
            pristineValue.childSiteCount = pristineProperties.childSiteCount || "0";
            pristineValue.updatedOn = this._toLocaleString(
                _.get(this._model._site, "updatedOn", "")
            );
            pristineValue.updatedBy = _.get(this._model._site, "_updatedBy", "");
            pristineValue.createdOn = this._toLocaleString(
                _.get(this._model._site, "createdOn", "")
            );
            pristineValue.createdBy = _.get(this._model._site, "createdBy", "");

            const declaredCustomFieldValues = _.pick(
                _.get(this._model._site.pristineProperties, "customFieldValues", {}),
                this._model.siteCustomFieldIds
            );
            if (!_.isEmpty(declaredCustomFieldValues)) {
                pristineValue.customFieldValues = declaredCustomFieldValues;
            }
            pristineValue.discoveryLevel = pristineProperties.discoveryLevel;
            pristineValue.credsName = _.get(this._model._site, "credsName", "");
        }
        return pristineValue;
    }

    // This method will get properties from root level of entity
    // It will get dirty state values if the entity has pending changes
    // Always Latest
    _getPristineValueLatest() {
        let pristineValue = {};
        const orgName = this._model._gameEditor._userSession.activeOrganization.name;
        if (this.model._site) {
            pristineValue.name = _.get(this._model._site, "name");
            pristineValue.codeName = _.get(this._model._site, "codeName");
            pristineValue.description = _.get(this._model._site, "description");
            pristineValue.path = _.get(this._model._site, "path", "/");
            if (pristineValue.path === orgName) {
                pristineValue.path = "/";
            }
            pristineValue.path = pristineValue.path.replace("/" + orgName + "/", "/");
            pristineValue.parentName = _.get(this._model._site, "parentName");
            pristineValue.adminContact = _.get(this._model._site, "adminContact");
            pristineValue.technicalContact = _.get(this._model._site, "technicalContact");
            pristineValue.location = _.get(this._model._site, "location");
            pristineValue.locationType = _.get(this._model._site, "locationType");
            pristineValue.address = _.get(this._model._site, "address");
            pristineValue.country = _.get(this._model._site, "country");
            pristineValue.latitude = _.get(this._model._site, "latitude");
            pristineValue.longitude = _.get(this._model._site, "longitude");
            pristineValue.timezone = _.get(this._model._site, "timezone");
            pristineValue.autoAssignContainer = {};
            pristineValue.autoAssignContainer.autoAssignEnabled = _.get(
                this._model._site,
                "autoAssignEnabled"
            );
            pristineValue.autoAssignContainer.autoAssignProperty = _.get(
                this._model._site,
                "autoAssignProperty"
            );
            pristineValue.autoAssignContainer.autoAssignRegex = _.get(
                this._model._site,
                "autoAssignRegex"
            );
            pristineValue.deviceCount = _.get(this._model._site, "deviceCount", "0");
            pristineValue.childSiteCount = _.get(this._model._site, "childSiteCount", "0");
            pristineValue.updatedOn = this._toLocaleString(_.get(this._model._site, "updatedOn"));
            pristineValue.updatedBy = _.get(this._model._site, "updatedBy");
            pristineValue.createdOn = this._toLocaleString(_.get(this._model._site, "createdOn"));
            pristineValue.createdBy = _.get(this._model._site, "createdBy", "");

            const declaredCustomFieldValues = _.pick(
                _.get(this._model._site.pristineProperties, "customFieldValues", {}),
                this._model.siteCustomFieldIds
            );
            if (!_.isEmpty(declaredCustomFieldValues)) {
                pristineValue.customFieldValues = declaredCustomFieldValues;
            }
            pristineValue.discoveryLevel = _.get(this._model._site, "discoveryLevel");
            pristineValue.credsName = _.get(this._model._site, "credsName", "");
        }
        return pristineValue;
    }

    _getJtype() {
        // Location type obj, update to get from entity
        const locTypes = {
            BRANCH: "Branch",
            BUILDING: "Building",
            CAMPUS: "Campus",
            COLO: "Colo",
            CSP: "CSP",
            DATA_CENTER: "Data center",
            HOME: "Home",
            INTERNET: "Internet",
            KIOSK: "Kiosk",
            PLANT: "Plant",
            SITE: "Site",
            STORE: "Store",
            WAREHOUSE: "Warehouse",
            ADMINISTRATION: "Administration",
            HOSPITAL: "Hospital",
            MOB: "MOB",
        };
        let jtype = {};
        const pathValues = Object.values(this._model._pathList);
        const pathLabels = Object.keys(this._model._pathList);
        // A Path value missing from its own enum resolves to null, and since the control is
        // required that reports invalid on every render. Keep the current parent selectable.
        const currentParentId = this._model._site.getParentId();
        if (currentParentId && pathValues.indexOf(currentParentId) < 0) {
            pathValues.push(currentParentId);
            pathLabels.push(_.get(this._model._siteNameMap, currentParentId, currentParentId));
        }
        let customFields = this._model._site.pristineProperties.customFieldValues;
        const customFieldValues = this.model.customFieldsJtypes;

        if (this._model._isRootSite) {
            jtype.name = "";
            jtype["name@Display.label"] = "Name";
            jtype["name@Display.readOnly"] = true;

            jtype.codeName = "";
            jtype["codeName@Display.label"] = "Code Name";
            jtype["codeName@Display.readOnly"] = true;

            jtype.lineBreak1 = null;
            jtype["lineBreak1@Display.label"] = "&nbsp;";

            jtype.path = "/";
            jtype["path@Enum"] = ["/"];
            jtype["path@Display.enum"] = ["/"];
            jtype["path@Display.label"] = "Path";
            jtype["path@Required"] = true;
            jtype["path@Display.readOnly"] = true;

            jtype.credsName = "";
            jtype["credsName@Enum"] = Object.keys(this._model.credOptions);
            jtype["credsName@Display.enum"] = Object.keys(this._model.credOptions);
            jtype["credsName@Display.label"] = "Credential";

            if (
                customFields &&
                !_.isEmpty(customFields) &&
                customFieldValues &&
                customFieldValues.length > 0
            ) {
                jtype.lineBreak3 = null;
                jtype["lineBreak3@Display.label"] = "&nbsp;";
                jtype.customFieldValues = {};
                Object.assign(jtype.customFieldValues, ...customFieldValues);
                jtype["customFieldValues@Display.label"] = "Custom Fields";
                jtype["customFieldValues@Display.readOnly"] = true;
            }

            jtype["discoveryLevel@Enum"] = Object.values(
                this._model._SiteEntity.getDiscoveryLevelValues()
            ).map(Number); //must convert to number, Object.keys won't auto convert either

            jtype["discoveryLevel@Display.enum"] = Object.values(
                this._model._SiteEntity.getDiscoveryLevelLabels()
            );
            jtype.discoveryLevel = "";
            jtype["discoveryLevel@Display.label"] = "Discovery Level";
            jtype["discoveryLevel@Required"] = true;

            jtype.lineBreak4 = null;
            jtype["lineBreak4@Display.label"] = "&nbsp;";

            jtype.deviceCount = "0";
            jtype["deviceCount@Display.label"] = "Devices";

            jtype.childSiteCount = "0";
            jtype["childSiteCount@Display.label"] = "Sites";

            jtype.lineBreak5 = null;
            jtype["lineBreak5@Display.label"] = "&nbsp;";

            jtype.updatedOn = "";
            jtype["updatedOn@Display.label"] = "Updated On";

            jtype.updatedBy = "";
            jtype["updatedBy@Display.label"] = "Updated By";

            jtype.lineBreak6 = null;
            jtype["lineBreak6@Display.label"] = "&nbsp;";
        } else {
            jtype.name = "";
            jtype["name@Display.label"] = "Name";

            jtype.codeName = "";
            jtype["codeName@Display.label"] = "Code Name";

            // jtype.uniqueName = "";
            // jtype["uniqueName@Display.label"] = "Unique Name";

            jtype.description = "";
            jtype["description@Display.label"] = "Description";

            jtype.lineBreak1 = null;
            jtype["lineBreak1@Display.label"] = "&nbsp;";

            jtype.path = this._model._pathList["/"];
            jtype["path@Enum"] = pathValues;
            jtype["path@Display.enum"] = pathLabels;
            jtype["path@Display.label"] = "Path";
            jtype["path@Required"] = true;

            jtype.parentName = "";
            jtype["parentName@Display.label"] = "Parent Site Name";

            jtype.lineBreak2 = null;
            jtype["lineBreak2@Display.label"] = "&nbsp;";

            jtype.adminContact = [];
            jtype["adminContact@Display.label"] = "Admin Contact";
            jtype["adminContact@Display.multiline"] = true;
            jtype["adminContact@Display.text"] = {minLineCount: 5};
            jtype["adminContact@Display.placeholder"] = "";

            jtype.technicalContact = [];
            jtype["technicalContact@Display.label"] = "Technical Contact";
            jtype["technicalContact@Display.multiline"] = true;
            jtype["technicalContact@Display.text"] = {minLineCount: 5};
            jtype["technicalContact@Display.placeholder"] = "";

            jtype.lineBreak3 = null;
            jtype["lineBreak3@Display.label"] = "&nbsp;";

            jtype.location = "";
            jtype["location@Display.label"] = "Location";

            jtype.locationType = "SITE";
            jtype["locationType@Display.label"] = "Type";

            const sortedLocTypes = Object.entries(locTypes).sort(([, a], [, b]) =>
                a.localeCompare(b)
            );
            jtype["locationType@Enum"] = sortedLocTypes.map(([key]) => key);
            jtype["locationType@Display.enum"] = sortedLocTypes.map(([, label]) => label);
            jtype["locationType@Required"] = true;

            jtype.address = "";
            jtype["address@Display.label"] = "Address";

            jtype.country = "";
            jtype["country@Display.label"] = "Country/Region";

            jtype.latitude = "";
            jtype["latitude@Display.label"] = "Latitude";

            jtype.longitude = "";
            jtype["longitude@Display.label"] = "Longitude";

            let offsetTmzLabels = [];
            let offsetTmzValues = [];
            timeZonesJSON.forEach((i) => {
                const offset = i.text.match(/\(([^)]+)\)/)[1],
                    value = i.value,
                    abbr = i.abbr;

                i.utc.forEach((utcTimezone) => {
                    if (abbr !== "U") {
                        offsetTmzLabels.push(`(${offset}) ${utcTimezone} - ${value} (${abbr})`);
                        offsetTmzValues.push(utcTimezone);
                    }
                });
            });

            jtype["timezone@Enum"] = offsetTmzValues;
            jtype["timezone@Display.enum"] = offsetTmzLabels;
            jtype["timezone@Display.label"] = "Timezone";

            jtype.credsName = "";
            jtype["credsName@Enum"] = Object.keys(this._model.credOptions);
            jtype["credsName@Display.enum"] = Object.keys(this._model.credOptions);
            jtype["credsName@Display.label"] = "Credential";

            jtype.autoAssignContainer = {};
            const autoAssignContainer = jtype.autoAssignContainer;
            jtype["autoAssignContainer@Display.label"] = "Auto-assign Devices";

            autoAssignContainer.autoAssignEnabled = false;
            autoAssignContainer["autoAssignEnabled@Display.label"] = "Enabled";

            const defaultValues = _.get(this._model, "_SiteEntity.autoAssignPropertyLabels", {});
            autoAssignContainer.autoAssignProperty = "NAME";
            autoAssignContainer["autoAssignProperty@Display.label"] = "Property";
            autoAssignContainer["autoAssignProperty@Enum"] = Object.keys(defaultValues);
            autoAssignContainer["autoAssignProperty@Display.enum"] = Object.values(defaultValues);
            autoAssignContainer["autoAssignProperty@Required"] = true;

            autoAssignContainer.autoAssignRegex = "0";
            autoAssignContainer["autoAssignRegex@Display.label"] = "RegEx";

            if (
                customFields &&
                !_.isEmpty(customFields) &&
                customFieldValues &&
                customFieldValues.length > 0
            ) {
                jtype.lineBreak3 = null;
                jtype["lineBreak3@Display.label"] = "&nbsp;";
                jtype.customFieldValues = {};
                Object.assign(jtype.customFieldValues, ...customFieldValues);
                jtype["customFieldValues@Display.label"] = "Custom Fields";
                jtype["customFieldValues@Display.readOnly"] = true;
            }

            jtype["discoveryLevel@Enum"] = Object.values(
                this._model._SiteEntity.getDiscoveryLevelValues()
            );
            jtype["discoveryLevel@Enum"].forEach((val, idx) => {
                //must convert to number, Object.keys won't auto convert either
                jtype["discoveryLevel@Enum"][idx] = Number(val);
            });
            jtype["discoveryLevel@Display.enum"] = Object.values(
                this._model._SiteEntity.getDiscoveryLevelLabels()
            );
            jtype.discoveryLevel = "";
            jtype["discoveryLevel@Display.label"] = "Discovery Level";
            jtype["discoveryLevel@Required"] = true;

            jtype.lineBreak4 = null;
            jtype["lineBreak4@Display.label"] = "&nbsp;";

            jtype.deviceCount = "0";
            jtype["deviceCount@Display.label"] = "Devices";

            jtype.childSiteCount = "0";
            jtype["childSiteCount@Display.label"] = "Sites";

            jtype.lineBreak5 = null;
            jtype["lineBreak5@Display.label"] = "&nbsp;";

            jtype.updatedOn = "";
            jtype["updatedOn@Display.label"] = "Updated On";

            jtype.updatedBy = "";
            jtype["updatedBy@Display.label"] = "Updated By";

            jtype.createdOn = "";
            jtype["createdOn@Display.label"] = "Created On";

            jtype.createdBy = "";
            jtype["createdBy@Display.label"] = "Created By";

            jtype.lineBreak6 = null;
            jtype["lineBreak6@Display.label"] = "&nbsp;";
        }

        // Update JType according to readOnly fields list
        readOnlyFieldIds.forEach(
            (fieldId) =>
                _.has(jtype, [fieldId]) && _.set(jtype, [`${fieldId}@Display.readOnly`], true)
        );
        return jtype;
    }

    _updateCustomBooleanWidth() {
        if (!this._rootElem) {
            console.warn("Root element not available");
            return;
        }
        const customFieldsEl = this._rootElem.querySelector(
            ".custom_field_values.rendered_control gl-form-renderer"
        );
        if (!customFieldsEl) {
            return;
        }
        if (!this._model._isRootSite) {
            customFieldsEl.classList.add("not-root-site");
        }
        const controlEls = customFieldsEl.querySelectorAll(".rendered_control");
        if (!controlEls || controlEls.length === 0) {
            return;
        }
        let largestTxtWidth = 0;
        controlEls.forEach((ctrl) => {
            let txtWidth = 0;
            if (ctrl.classList.contains("boolean")) {
                txtWidth = ctrl.querySelector("input-checkbox-control").textContent.length * 7;
            } else {
                txtWidth = ctrl.querySelector("label").textContent.length * 7;
            }
            if (txtWidth > largestTxtWidth) {
                largestTxtWidth = txtWidth;
            }
        });
        if (largestTxtWidth > 250) {
            largestTxtWidth = 250; // max allowed width
        }
        controlEls.forEach((ctrl) => {
            let txtCtrl;
            if (ctrl.classList.contains("boolean")) {
                let checkBoxWidth = (largestTxtWidth + 25) / 10;
                txtCtrl = ctrl.querySelector("input-checkbox-control");
                txtCtrl.style.width = checkBoxWidth + "rem";
            } else {
                txtCtrl = ctrl.querySelector("label");
                const txtCtrlWidth = largestTxtWidth / 10;
                txtCtrl.style.minWidth = txtCtrlWidth + "rem";
                txtCtrl.style.maxWidth = "25rem";
            }
            txtCtrl.setAttribute("title", txtCtrl.textContent);
        });
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = SiteDetailsViewRenderer;
